// @flow
import moment from "moment";
import { propComparator } from "../../../global/functions/StringOperations";

function compileNodeCompanyType2Json(company: *, isAdmin?: boolean = false): * {
  let level = null;
  if (company.companyType === "DISTRIBUTER") {
    level = "D";
  } else if (company.companyType === "RESELLER") {
    level = "R";
  } else if (company.companyType === "ENDUSER") {
    level = "E";
  } else if (company.companyType === "TOPLEVEL") {
    level = "I";
  }

  let authorisedReseller = null;
  if (company.authorisedReseller === "TRUE") {
    authorisedReseller = 1;
  } else if (company.authorisedReseller === "UNCONFIRMED") {
    authorisedReseller = 0;
  } else {
    authorisedReseller = -1;
  }

  if (isAdmin) {
    let authorised = null;
    let parent = company.parent;
    if (parent) {
      if (company.parent.authorisedReseller === "TRUE") {
        authorised = 1;
      } else if (company.parent.authorisedReseller === "UNCONFIRMED") {
        authorised = 0;
      } else if (company.parent.authorisedReseller === "FALSE") {
        authorised = -1;
      }
    }
    return {
      level: level,
      authorisedReseller: authorisedReseller,
      authorised: authorised
    };
  } else {
    return { level: level, authorisedReseller: authorisedReseller };
  }
}

export function buildCompanyInfoJson(company: *, isAdmin?: boolean = false): * {
  let json = {};
  json.id = company.id;
  json.name = company.name;
  json.uuid = company.uuid;
  json.contactName = company.contactName;
  json.contactNumber = company.contactNumber;
  json.contactEmail = company.contactEmail;
  json.notes = company.notes;
  json.abn = company.abn;

  json.postcode = company.postcode || "";
  json.address = company.address || "";
  json.suburb = company.suburb || "";
  json.city = company.city || "";
  json.state = company.state || "";
  json.country = company.country || "";

  let graphNode = compileNodeCompanyType2Json(company);
  json.level = graphNode.level;
  json.authorisedReseller = graphNode.authorisedReseller;

  let authorised = null;
  if (company.reseller === "TRUE") {
    authorised = 1;
  } else if (company.reseller === "UNCONFIRMED") {
    authorised = 0;
  } else {
    authorised = -1;
  }
  json.authorised = authorised;

  json.licenceLimit = company.licenceLimit;

  if (isAdmin) {
    json.auditCreationDate = company.auditCreationDate;
    json.createdOnDisplay = company.auditCreationDate
      ? moment
          .utc(company.auditCreationDate)
          .local()
          .format("DD/MMM/YYYY hh:mm A")
      : "";
    json.auditCreatedBy = company.auditCreatedBy;
    json.lastUpdatedDate = company.lastUpdatedDate;
    json.lastUpdatedOnDisplay = company.lastUpdatedDate
      ? moment
          .utc(company.lastUpdatedDate)
          .local()
          .format("DD/MMM/YYYY hh:mm A")
      : "";
    json.lastUpdatedBy = company.lastUpdatedBy;
    json.groupName = company.groupName;
    json.specialType = company.specialType;

    let parent = null;
    if (company.parent) {
      parent = {};
      parent.id = company.parent.id;
      parent.name = company.parent.name;
      parent.uuid = company.parent.uuid;
      let parentNode = compileNodeCompanyType2Json(company.parent);
      parent.level = parentNode.level;
      parent.authorisedReseller = parentNode.authorisedReseller;
    }
    json.parent = parent;

    json.tierId = company.tier && company.tier.id;
    json.tierName = company.tier && company.tier.name;

    json.productSheetId = company.productSheet && company.productSheet.id;
    json.productSheetName =
      company.productSheet && company.productSheet.description;
  }

  return json;
}

export function buildCompanyTreeJson(
  companyObject,
  isAdmin?: boolean = false
): * {
  let company = {};
  company.id = companyObject.id;
  company.name = companyObject.name;
  company.uuid = companyObject.uuid;
  let licences = [];
  const licenceObject = companyObject.licences && companyObject.licences.edges;
  if (licenceObject) {
    licenceObject
      .sort(propComparator("node", false, "expiryDate", null, true))
      .reverse();

    for (let i = 0; i < licenceObject.length; i++) {
      licences.push(licenceObject[i].node);
    }
  }
  let children = [];
  const childrenObject = companyObject.children && companyObject.children.edges;
  if (childrenObject) {
    childrenObject.sort(propComparator("node", true, "name"));

    for (let i = 0; i < childrenObject.length; i++) {
      children.push(childrenObject[i].node);
    }
  }
  let facebases = [];
  const facebaseObject =
    companyObject.facebases && companyObject.facebases.edges;
  if (facebaseObject) {
    facebaseObject.sort(propComparator("node", true, "name"));

    for (let i = 0; i < facebaseObject.length; i++) {
      facebases.push(facebaseObject[i].node);
    }
  }
  let users = [];
  const usersObject = companyObject.users && companyObject.users.edges;
  if (usersObject) {
    usersObject.sort(propComparator("node", true, "fullName"));

    for (let i = 0; i < usersObject.length; i++) {
      users.push(usersObject[i].node);
    }
  }

  if (isAdmin) {
    let quotes = [];
    const quotesObject = companyObject.quotes && companyObject.quotes.edges;
    if (quotesObject) {
      for (let i = 0; i < quotesObject.length; i++) {
        quotes.push(quotesObject[i].node);
      }
    }

    let projects = [];
    const projectsObject =
      companyObject.projects && companyObject.projects.edges;
    if (projectsObject) {
      for (let i = 0; i < projectsObject.length; i++) {
        projects.push(projectsObject[i].node);
      }
    }

    return {
      company: company,
      licences: licences,
      children: children,
      facebases: facebases,
      users: users,
      quotes: quotes,
      projects: projects
    };
  }

  return {
    company: company,
    licences: licences,
    children: children,
    facebases: facebases,
    users: users
  };
}

export function buildLicenceItemJson(
  licenceNode: *,
  isAdmin?: boolean = false,
  quoteCompany?: boolean = false
): * {
  let json = {};
  json.id = licenceNode.id;
  json.uuid = licenceNode.uuid;
  json.facebaseName = licenceNode.facebase.name;
  json.facebaseUuid = licenceNode.facebase.uuid;
  json.facebaseLimit = licenceNode.facebase.personLimit;

  json.licenceType = licenceNode.licenceType;
  json.noOfStreams = licenceNode.noOfStreams;
  json.autoAssign = licenceNode.autoAssign;
  json.authorisedReseller = licenceNode.authorisedReseller;

  const devices = licenceNode.devices.edges;
  json.slotsUsed = Number(devices.length) || 0;
  json.slotsTotal = Number(licenceNode.deviceLimit);
  json.slotsAvail = Number(json.slotsTotal - json.slotsUsed);

  let deviceList = [];
  if (devices) {
    for (let i = 0; i < devices.length; i++) {
      deviceList.push(devices[i].node);
    }
  }
  json.devices = deviceList;

  const expiryDate = moment.utc(licenceNode.expiryDate).local();
  json.expiryDate = expiryDate;
  json.expiryDateOrigin = licenceNode.expiryDate;

  json.startDate = moment
    .utc(licenceNode.startDate)
    .local()
    .format("DD/MMM/YYYY hh:mm A");
  json.startDateOrigin = licenceNode.startDate;
  json.expiryMinutes = expiryDate.diff(moment.utc().local(), "minutes");

  json.projectId = licenceNode.project && licenceNode.project.id;
  json.poFile = licenceNode.poFile;
  json.extentDate = licenceNode.maintenanceExpiryDate
    ? moment.utc(licenceNode.maintenanceExpiryDate).local()
    : null;
  json.extentDateOrigin = licenceNode.maintenanceExpiryDate;

  let histories = [];
  const historyObject =
    licenceNode.licenceExtents && licenceNode.licenceExtents.edges;
  if (historyObject) {
    historyObject
      .sort(propComparator("node", false, "auditCreationDate", null, true));
    historyObject.reverse();

    for (let i = 0; i < historyObject.length; i++) {
      const item = historyObject[i].node;

      const history = {
        id: item.id,
        uuid: item.uuid,
        licenceType: item.licenceType,
        noOfStreams: item.noOfStreams,
        autoAssign: item.autoAssign,
        noOfSlots: item.noOfSlots,
        expiryDate: moment.utc(item.expiryDate).local(),
        expiryDateOrigin: item.expiryDate,
        startDate: moment
          .utc(item.startDate)
          .local()
          .format("DD/MMM/YYYY hh:mm A"),
        startDateOrigin: item.startDate,
        expiryMinutes: moment
          .utc(item.expiryDate)
          .local()
          .diff(moment.utc().local(), "minutes"),
        poFile: item.poFile,
        extentDate: item.maintenanceExpiryDate
          ? moment.utc(item.maintenanceExpiryDate).local()
          : null,
        extentDateOrigin: item.maintenanceExpiryDate,
        createdBy: item.auditCreatedBy,
        createDateOrigin: item.auditCreationDate,
        createDate: item.auditCreationDate
          ? moment
              .utc(item.auditCreationDate)
              .local()
              .format("DD/MMM/YYYY hh:mm A")
          : null,
        deletedBy: item.auditDeletedBy,
        modules: item.modules
      };
      histories.push(history);
    }
  }
  json.histories = histories;

  json.modules = licenceNode.modules;

  if (isAdmin) {
    json.createdBy = licenceNode.auditCreatedBy;
    json.createDateOrigin = licenceNode.auditCreationDate;
    json.createDate = licenceNode.auditCreationDate
      ? moment
          .utc(licenceNode.auditCreationDate)
          .local()
          .format("DD/MMM/YYYY hh:mm A")
      : null;
    json.updatedBy = licenceNode.lastUpdatedBy;
    json.updatedDateOrigin = licenceNode.lastUpdatedDate;
    json.updatedDate = licenceNode.lastUpdatedDate
      ? moment
          .utc(licenceNode.lastUpdatedDate)
          .local()
          .format("DD/MMM/YYYY hh:mm A")
      : null;

    let graphNode = compileNodeCompanyType2Json(licenceNode.company);
    json.company = {
      id: licenceNode.company.id,
      uuid: licenceNode.company.uuid,
      name: licenceNode.company.name,
      level: graphNode.level,
      licenceLimit: licenceNode.company.licenceLimit
    };
  }

  if (quoteCompany) {
    json.company = { ...licenceNode.company };
  }
  return json;
}

export function buildCompanyFormJson(company): * {
  // from buildCompanyInfoJson(company)
  let json = {};
  json.id = company.id;
  json.uuid = company.uuid;
  json.name = company.name;
  json.level = company.level;
  json.authorised = company.authorised;
  json.licenceLimit = company.licenceLimit;
  return json;
}

export function buildAdminCompanyFormJson(company): * {
  let json = {};
  json.id = company.id;
  json.name = company.name;

  let graphNode = compileNodeCompanyType2Json(company, true);
  json.level = graphNode.level;
  json.authorised = graphNode.authorised;
  json.licenceLimit = company.licenceLimit;
  return json;
}

export function buildAdminCompanyHeading(
  company: *,
  isAdmin?: boolean = false
): * {
  let json = {};
  json.id = company.id;
  json.name = company.name;
  json.uuid = company.uuid;

  let graphNode = compileNodeCompanyType2Json(company);
  json.level = graphNode.level;
  json.authorisedReseller = graphNode.authorisedReseller;

  if (isAdmin) {
    json.licenceCount = company.licences.edges && company.licences.edges.length;
    json.tierId = company.tier ? company.tier.id : "";
    json.productSheetId = company.productSheet ? company.productSheet.id : "";
    json.specialType = company.specialType;
  }
  return json;
}

export const companyDialogMessages = [
  "Facebase has been successfully created",
  "Company has been successfully created",
  "Email has been successfully sent to Invited User.",
  "Licence has been successfully created",
  "Company Information has been successfully changed",
  "Licence has been successfully changed",
  "Licence has been successfully deleted",
  "Project has been successfully updated",
  "Project has been successfully deleted",
  "Quote has been successfully updated",
  "Quote has been successfully deleted",
  "User has been permanently deleted",
  "User has been successfully updated",
  "New User has been successfully created",
  "Registration Email has resent to the user",
  "Company has been permanently deleted",
  "Facebase has been permanently deleted",
  "Quote has been successfully linked",
  "Licence has been successfully linked",
  "Invoice file has been successfully uploaded"
];

export function buildFacebaseLicenceItemJson(licence: *): * {
  let json = {};
  json.uuid = licence.uuid;
  json.licenceType = licence.licence_type;
  json.noOfStreams = licence.no_of_streams;

  json.slotsUsed = Number(licence.used_slots);
  json.slotsTotal = Number(licence.device_limit);
  json.slotsAvail = Number(json.slotsTotal - json.slotsUsed);

  const expiryDate = moment.utc(licence.expiry_date).local();
  json.expiryDate = expiryDate;
  json.startDate = moment
    .utc(licence.start_date)
    .local()
    .format("DD/MMM/YYYY hh:mm A");
  json.expiryMinutes = expiryDate.diff(moment.utc().local(), "minutes");
  return json;
}

export function buildFacebaseItemJson(
  facebase: *,
  isAdmin?: boolean = false
): * {
  let json = {};
  json.uuid = facebase.uuid;
  json.name = facebase.name;
  json.online = facebase.online;
  json.personLimit = facebase.person_limit || facebase.personLimit;
  json.model = facebase.model;
  if (isAdmin) {
    json.peopleCount = facebase.people_count;
    json.faceCount = facebase.face_count;
    json.licences = facebase.licences;
    json.users = facebase.users;
  }

  return json;
}

export function buildAdminUserItemJson(user: *): * {
  let json = {};
  json.uuid = user.uuid;
  json.user_status = user.user_status;
  json.email = user.email;
  json.first_name = user.first_name;
  json.last_name = user.last_name;
  json.fullName = (user.first_name || "") + " " + (user.last_name || "");
  json.facebase = {
    uuid: user.facebase_uuid,
    name: user.facebase_name
  };
  json.company = {
    uuid: user.company_uuid,
    name: user.company
  };

  json.groups = user.groups;

  json.audit_creation_date = user.audit_creation_date
    ? moment
        .utc(user.audit_creation_date)
        .local()
        .format("DD/MMM/YYYY hh:mm A")
    : "";
  json.audit_created_by = user.audit_created_by || "";
  json.last_updated_date = user.last_updated_date
    ? moment
        .utc(user.last_updated_date)
        .local()
        .format("DD/MMM/YYYY hh:mm A")
    : "";
  json.last_updated_by = user.last_updated_by || "";
  json.last_logged_in = user.last_logged_in
    ? moment
        .utc(user.last_logged_in)
        .local()
        .format("DD/MMM/YYYY hh:mm A")
    : "Never Logged In";

  return json;
}

export function buildAdminUserNodefromGraphQlNode(
  jsonUser: *,
  presetEmail?: string,
  presetStatus?: string,
  presetGroups?: Array<string>
): * {
  //compile graphql return user object to /auth/v2/users user object, the list should also change to user graphql for consistant
  let user = {};
  user.first_name = jsonUser.firstName;
  user.last_name = jsonUser.lastName;
  user.uuid = jsonUser.cognitoUuid;

  user.facebase_name = jsonUser.defaultFacebase.name;
  user.facebase_uuid = jsonUser.defaultFacebase.uuid;
  user.company = jsonUser.company.name;
  user.company_uuid = jsonUser.company.uuid;

  user.audit_creation_date = jsonUser.auditCreationDate;
  user.audit_created_by = jsonUser.auditCreatedBy;
  user.last_updated_date = jsonUser.lastUpdatedDate;
  user.last_updated_by = jsonUser.lastUpdatedBy;
  user.last_logged_in = jsonUser.lastLoggedIn;

  if (presetEmail) {
    user.email = presetEmail;
  } else {
    user.email = jsonUser.email;
  }

  if (presetStatus) {
    user.user_status = presetStatus;
  } else {
    user.user_status = jsonUser.userStatus;
  }

  if (presetGroups) {
    user.groups = presetGroups;
  } else {
    user.groups = jsonUser.groups;
  }

  return user;
}
